import { skipToken } from '@apollo/client/react'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router'
import IcBaselineChevronLeft from '~icons/ic/baseline-chevron-left'
import Empty from 'src/components/Empty'
import LoadingPage from 'src/components/LoadingPage'
import NewsArticle from 'src/features/NewsArticle/NewsArticle'
import type { NewsArticleDataType } from 'src/features/NewsArticle/util'
import { getNewsArticleData } from 'src/features/NewsArticle/util'
import PaperLayout from 'src/layouts/PaperLayout'
import type { Notification } from 'src/services/apollo/generated/graphql'
import { useGetNotification } from 'src/services/apollo/notification'
import { localeAtom } from 'src/stores/locale'
import { cx } from 'src/utils/cx'

export { NewsDetailPage as Component }
function NewsDetailPage(props: {
  initialNotification?: Notification
  initialArticleData?: NewsArticleDataType
}) {
  const locale = useAtomValue(localeAtom)
  const { t } = useTranslation(['common', 'artwork'])
  const { id } = useParams<{ id: string }>()
  const { data, loading } = useGetNotification(id ?? skipToken)

  // SSR-friendly notification resolution: only swap to live `data`
  // once it carries the full payload (`updatedAt` + `locales`); a
  // partial response keeps the loader's `initialNotification` so the
  // header / article body don't blank during the refetch window.
  const resolvedNotification =
    data?.updatedAt && data?.locales
      ? data
      : (props.initialNotification ?? data)

  // Localized article data: prefer the loader's pre-localized
  // `initialArticleData` when present, otherwise derive from the
  // resolved Notification + active locale. Wrapped in `useMemo` so
  // the article body doesn't re-render on unrelated locale-atom
  // subscribers (matches main's behaviour after the
  // `useTranslation` → `localeAtom` migration).
  const articleData = useMemo(
    () =>
      props.initialArticleData ??
      getNewsArticleData(locale, resolvedNotification),
    [locale, resolvedNotification, props.initialArticleData],
  )

  /*
   * Render
   */

  return (
    <>
      {/*
        News article meta tags (title / description / og:url) are
        emitted from the route's `meta()` export in
        `routes/_app.news.$id.tsx` so they land in `<head>` during the
        first SSR flush, instead of depending on a body-level
        `<MetaTags>` that only commits after hydration.
      */}
      <PaperLayout
        className="-mx-4 msm:-mx-6 mmd:mx-auto"
        header={({ className }) => (
          <header
            className={cx(
              'p-4 flex items-center bg-white/50 dark:bg-black/20 mmd:!bg-transparent border-b border-black/10 dark:border-white/10',
              className,
            )}
          >
            <Link
              to="/news"
              className="inline-flex items-center gap-1 text-sm text-theme-primary"
            >
              <IcBaselineChevronLeft className="size-5" />
              {t('common:news.buttons.back-to-all.label')}
            </Link>
          </header>
        )}
      >
        <div className="px-4 msm:px-6 mmd:px-0">
          {resolvedNotification && articleData ? (
            <NewsArticle
              updatedAt={resolvedNotification.updatedAt}
              articleData={articleData}
            />
          ) : loading ? (
            <LoadingPage />
          ) : (
            <Empty description={t('artwork:no-content')} />
          )}
        </div>
      </PaperLayout>
    </>
  )
}
